import React, { useState } from 'react';
import { isEmpty, noop } from 'lodash';
import { Container } from '@fiverr-private/layout_components';
import { useRequireCollectionsProvider } from '../../hooks/useRequireCollectionsProvider';
import {
    withCollectionsProvider,
    useCollectionsContext,
    CollectionsComponentProps,
} from '../../context/CollectionsContext';
import { CollectionMenuOption, RawCollection, ResourceType, UpdatedCollectionData } from '../../types';
import { renderSaveToListTrigger, RenderTriggerProps } from '../SaveToListButton/SaveToListButton';
import { AddEditListModal } from '../AddEditListModal/AddEditListModal';
import { useResourceCollections } from '../../hooks/useResourceCollections';
import { isSignUpOrMigrationRequired } from '../../utils/isSignUpOrMigrationRequired';
import {
    sendAddItemToListEvent,
    sendClickCreateNewListEvent,
    sendRemoveItemFromListEvent,
} from '../../utils/events/freelancersListEvents';
import { logNonAxiosError } from '../../utils/logUtils';
import { SaveToListView } from './SaveToListView';

export type ButtonSize = React.ComponentProps<typeof import('@fiverr-private/button').Button>['size'];

export interface ButtonProps {
    onClick: () => void;
    size?: ButtonSize;
    disabled?: boolean;
}

export interface SaveToListPopoverProps {
    resourceType: ResourceType;
    resourceId: string;
    initialIsOpen?: boolean;
    currentCollectionId?: string;
    fullWidth?: boolean;
    renderTrigger?: (props: RenderTriggerProps) => React.ReactElement<ButtonProps>;
    position?: React.ComponentProps<typeof import('@fiverr-private/dropdown').Dropdown>['position'];
    bi?: CollectionsComponentProps['bi'];
    initialCollections?: RawCollection[];
    onAddItemToCollection?: (updatedItem: UpdatedCollectionData) => void;
    onRemoveItemFromCollection?: (updatedItem: UpdatedCollectionData) => void;
}

export const SaveToListPopoverBase = ({
    resourceType,
    resourceId,
    initialIsOpen = false,
    currentCollectionId,
    fullWidth,
    renderTrigger = renderSaveToListTrigger,
    position,
    bi: biProps,
    initialCollections: initialCollectionsProps,
    onAddItemToCollection = noop,
    onRemoveItemFromCollection = noop,
}: SaveToListPopoverProps) => {
    useRequireCollectionsProvider('SaveToListPopover');
    const [isOpen, setIsOpen] = useState(initialIsOpen && !isSignUpOrMigrationRequired());
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { bi, initialCollections, triggerData } = useCollectionsContext({
        overrides: { bi: biProps, initialCollections: initialCollectionsProps },
    });
    const { addToCollection, removeFromCollection, collectionOptions, isLoading } = useResourceCollections({
        resourceType,
        resourceId,
        currentCollectionId,
        initialCollections,
    });
    const hasMenuOptions = !isEmpty(collectionOptions);

    // The modal could be open because the user explicitly clicked create,
    // or because they clicked Save and there are no collections yet.
    const showingModal = !isLoading && (isModalOpen || (isOpen && !hasMenuOptions));

    const openEditModal = () => {
        setIsModalOpen(true);

        sendClickCreateNewListEvent({ biContext: bi });
    };

    const handleChange = (collection: CollectionMenuOption) => {
        const biProps = {
            biContext: bi,
            gigId: resourceType === 'GIG' ? resourceId : undefined,
            sellerId: resourceType === 'SELLER' ? resourceId : undefined,
            collection: {
                id: collection.id,
                type: collection.isPrivate ? 'USER' : 'BUSINESS',
                name: collection.name,
                item: {
                    id: resourceId,
                    type: resourceType,
                },
            },
        } satisfies Parameters<typeof sendAddItemToListEvent>[0];
        const updatedItemData = { collection, resourceId, resourceType };
        if (collection.checked) {
            addToCollection(collection.id)
                .then(() => {
                    onAddItemToCollection(updatedItemData);
                    sendAddItemToListEvent(biProps);
                })
                .catch((error: Error) => logNonAxiosError(error, 'Failed to add item to collection'));
        } else {
            removeFromCollection(collection.id)
                .then(() => {
                    onRemoveItemFromCollection(updatedItemData);
                    sendRemoveItemFromListEvent(biProps);
                })
                .catch((error: Error) => logNonAxiosError(error, 'Failed to remove item from collection'));
        }
    };

    const handleCreateModalOpenChange = (nextIsModalOpen) => {
        if (nextIsModalOpen) {
            setIsModalOpen(true);
        } else {
            setIsModalOpen(false);
            setIsOpen(false);
        }
    };

    return (
        <Container>
            <SaveToListView
                resourceType={resourceType}
                resourceId={resourceId}
                onChange={handleChange}
                onOpenEditModal={openEditModal}
                currentCollectionId={currentCollectionId}
                renderTrigger={renderTrigger}
                fullWidth={fullWidth}
                isOpen={isOpen}
                onOpenChange={setIsOpen}
                position={position}
                bi={bi}
                triggerData={triggerData}
            />
            {/* We need to render the modal here rather than using the trigger deeper in the tree,
            since otherwise the modal would disappear when the window is resized. */}
            <AddEditListModal
                isOpen={showingModal}
                onOpenChange={handleCreateModalOpenChange}
                resourceType={resourceType}
                resourceId={resourceId}
            />
        </Container>
    );
};

export const SaveToListPopover = withCollectionsProvider(SaveToListPopoverBase);
